import React, { useCallback, useEffect, useMemo } from 'react';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/styles';

import styles from '../styles/subscriptionData';
import DataRow from '../../../partials/Datarow/DataRow';
import Optional from '../../../utils/optional';
import StatusCell from '../../../partials/statusCell/StatusCell';
import pickOptionsForStatusCell from '../../../partials/statusCell/actions/pickOptionsForStatusCell';
import STATUS_BOX_OPTIONS from '../../../consts/statusBoxOptions';
import { isArray, isDefined, isUndefined } from '../../../utils/isDefined';
import getSubscriptions from '../actions/getSubscriptions';
import { actions as reduxActions } from '../reducers';
import Problems from './Problems';
import RefreshButton from '../../../partials/tableCustomButtons/RefreshButton';
import { translate } from '../../../i18n/I18nProvider';
import Features from './Features';
import { CircularProgress } from '@material-ui/core';
import SubscriptionMenu from './SubscriptionMenu';
import withSettings from '../../../utils/withSettings';
import clsx from 'clsx';
import ExclamationTriangleIcon from '../../../partials/customIcons/ExclamationTriangleIcon';
import capitaliseFirstLetter from '../../../utils/capitaliseFirstLetter';

const SubscriptionData = ({
  selectedNumber,
  classes,
  subscription,
  clientId,
  lang,
  settings,
  subscriptionReload,
  setSubscriptionReload,
  subscriptionLoading,
  setSubscriptionLoading
}) => {
  useEffect(() => {
    (async () => {
      await getSubscriptions({
        number: selectedNumber.value,
        clientId,
        setIsLoading: setSubscriptionLoading
      });
    })();
    // eslint-disable-next-line
  }, [selectedNumber, clientId]);

  useEffect(() => {
    (async () => {
      if (subscriptionReload) {
        await getSubscriptions({
          number: selectedNumber.value,
          clientId,
          setIsLoading: setSubscriptionLoading
        });

        setSubscriptionReload(false);
      }
    })();
    // eslint-disable-next-line
  }, [selectedNumber, clientId, subscriptionReload]);

  const checkRefillStatus = useCallback((projection = []) => {
    const refill = projection.find(el => el.name === 'Refill');

    if (isUndefined(refill)) {
      return false;
    }

    return refill.value;
  }, []);

  const customClasses = useMemo(() => {
    return {
      dataRow: {
        root: classes.dataRow_root,
        value: classes.dataRow_value
      }
    };
  }, [classes]);

  return (
    <div className={classes.root}>
      <header className={classes.title}>
        {translate('REQUEST_NEW_USER.SUBSCRIPTION')}
        <div className={classes.titleActions}>
          <RefreshButton
            onClick={() => setSubscriptionReload(true)}
            isSpinning={subscriptionReload}
          />
          <SubscriptionMenu
            subscription={subscription}
            clientId={clientId}
            setRefresh={setSubscriptionReload}
          >
            <i
              className={clsx('fas fa-ellipsis-h', classes.dotsMenuIcon)}
              style={{ color: settings.colors.primary }}
            ></i>
          </SubscriptionMenu>
        </div>
      </header>
      {subscriptionLoading && (
        <div className={classes.overlay}>
          <CircularProgress />
        </div>
      )}
      {isUndefined(subscription) ? (
        <div className={classes.textContainer}>
          <div> There is no billing data for this number</div>
        </div>
      ) : (
        <div className={classes.contentContainer}>
          <DataRow
            label={`${translate('REQUEST_NEW_USER.SUBSCRIPTION')} `}
            value={findSkuTranslation({
              subscription,
              lang,
              key: 'sku'
            })}
            customInfoIcon={
              isDefined(subscription?.migration)
                ? ExclamationTriangleIcon
                : null
            }
            customIconProps={{
              customFontSize: '14px'
            }}
            customTooltipText={
              isDefined(subscription?.migration) ? (
                <div>
                  <div>{`${capitaliseFirstLetter(
                    translate('SUBSCRIPTIONS.CURRENTLY_IN_MIGRATION')
                  )} ${findSkuTranslation({
                    subscription,
                    lang,
                    key: 'migrationSku'
                  })}`}</div>
                  <div>{`${translate('SUBSCRIPTIONS.CHANGE_DATE')} : ${
                    subscription?.migration.changeDate
                  }`}</div>
                </div>
              ) : null
            }
            customClasses={customClasses.dataRow}
            dense
          />
          <DataRow
            label={`${translate('NAME')} `}
            value={Optional(subscription?.name).or('')}
            customClasses={customClasses.dataRow}
            dense
          />
          {subscription?.status !== 'ACTIVE' && (
            <DataRow
              label={`${translate('STATUS')} `}
              value={
                <StatusCell
                  isTable={false}
                  {...pickOptionsForStatusCell(
                    STATUS_BOX_OPTIONS.SUBSCRIPTIONS_STATUS_OPTIONS,
                    subscription?.status
                  )}
                />
              }
              customClasses={customClasses.dataRow}
              dense
            />
          )}
          <DataRow
            label={`${translate('SUBSCRIPTIONS.REFILL')} `}
            value={
              <StatusCell
                isTable={false}
                {...pickOptionsForStatusCell(
                  STATUS_BOX_OPTIONS.SIMCARD_ACTIVE,
                  checkRefillStatus(subscription?.projection)
                )}
              />
            }
            customClasses={customClasses.dataRow}
            dense
          />
          {isArray(subscription?.features) && (
            <Features features={subscription?.features} />
          )}
          {isArray(subscription?.problems) && (
            <Problems problems={subscription?.problems} />
          )}
        </div>
      )}
    </div>
  );
};

const mapStatesToProps = ({ user, auth }) => {
  const {
    selectedNumber = {},
    client_id,
    subscription,
    subscriptionReload,
    subscriptionLoading
  } = user;

  return {
    selectedNumber,
    clientId: client_id,
    subscription,
    subscriptionReload,
    subscriptionLoading,
    lang: auth.lang
  };
};

const mapDispatchToProps = {
  setSubscription: reduxActions.setSubscription,
  setSubscriptionReload: reduxActions.setSubscriptionReload,
  setSubscriptionLoading: reduxActions.setSubscriptionLoading
};

export default flowRight(
  withSettings,
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(SubscriptionData);

const findSkuTranslation = ({ subscription, lang, key }) => {
  return Optional(subscription?.translations?.[key]?.[lang]).or(
    Optional(subscription?.translations?.[key]?.default).or('')
  );
};
